import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

/**
 * PWA "Add to Home Screen" prompt.
 * - Chrome/Android: reads `window.__deferredInstallPrompt` (captured very
 *   early in index.js) and listens for the `micm-install-available` custom
 *   event, so the prompt survives across route changes and shows up even
 *   when the user lands on a non-portal page first.
 * - iOS Safari: shows manual "Share → Add to Home Screen" instructions
 *   (iOS does not expose the standard install API).
 * - Dismissed state persists for 30 days in localStorage.
 */
const DISMISS_KEY = "micm_install_dismissed_at";
const DISMISS_DAYS = 30;

export default function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
        if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400 * 1000) return;

        const ua = window.navigator.userAgent || "";
        const iOS = /iphone|ipad|ipod/i.test(ua);
        const inStandalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (inStandalone) return;

        if (iOS) {
            setIsIOS(true);
            setIsVisible(true);
            return;
        }

        // 1) Consume any event captured before this component mounted
        if (window.__deferredInstallPrompt) {
            setDeferredPrompt(window.__deferredInstallPrompt);
            setIsVisible(true);
        }

        // 2) Also listen for late-firing events (in case Chrome delays it)
        const onAvailable = () => {
            if (window.__deferredInstallPrompt) {
                setDeferredPrompt(window.__deferredInstallPrompt);
                setIsVisible(true);
            }
        };
        window.addEventListener("micm-install-available", onAvailable);

        // 3) Direct listener as an additional safety net for pages that mount early
        const handler = (e) => {
            e.preventDefault();
            window.__deferredInstallPrompt = e;
            setDeferredPrompt(e);
            setIsVisible(true);
        };
        window.addEventListener("beforeinstallprompt", handler);

        return () => {
            window.removeEventListener("micm-install-available", onAvailable);
            window.removeEventListener("beforeinstallprompt", handler);
        };
    }, []);

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        window.__deferredInstallPrompt = null;
        setDeferredPrompt(null);
        setIsVisible(false);
        if (outcome !== "accepted") {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        }
    };

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div
            data-testid="install-prompt"
            className="mb-6 rounded-lg border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-white p-4 shadow-lg ring-2 ring-blue-100"
        >
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">
                📱 Recommended · Install MICM
            </div>
            <div className="flex items-start gap-3">
                <img
                    src="/logo192.png"
                    alt="MD Impex India"
                    className="hidden h-14 w-14 shrink-0 rounded-md object-cover ring-1 ring-slate-200 sm:block"
                />
                <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="font-display text-base font-semibold text-slate-900">
                            Install <span className="brand-gold brand-gold-shimmer" style={{ fontSize: "1rem", letterSpacing: "0.06em" }}>MD Impex India</span> on your phone
                        </div>
                        <button
                            data-testid="install-dismiss"
                            onClick={dismiss}
                            aria-label="Dismiss"
                            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                        {isIOS ? (
                            <>Tap the <b>Share</b> button in Safari and choose <b>Add to Home Screen</b> — the MD Impex app icon will appear on your home screen for one-tap access.</>
                        ) : (
                            <>Get quick access — install the MD Impex India app on your home screen for a native-like experience.</>
                        )}
                    </p>
                    {!isIOS && (
                        <button
                            data-testid="install-btn"
                            onClick={install}
                            className="mt-3 inline-flex items-center gap-2 rounded-md bg-blue-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-blue-700"
                        >
                            <DownloadSimple size={16} weight="bold" />
                            Install App
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
